import { Badge, Button, Input } from "@mantine/core";
import { Collapse, Modal, Progress, Select, Table, Typography } from "antd";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { IconMail, IconPencil, IconPlus } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { IconGripVertical } from "@tabler/icons-react";
import WorkItemDialog from "@/components/WorkItemDialog";
import type { WorkItemDialogValue } from "@/components/WorkItemDialog";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useTranslation } from "react-i18next";

const { Panel } = Collapse;

interface WorkItem {
  id: string;
  name: string;
  employees: Employee[];
  scanMode: string;
  documentId: string;
  projectNodeId: string;
  scanHistory?: ScanHistoryItem[];
  order?: number;
  reportPresetTexts?: { texts: string[] } | null;
}

interface ScanHistoryItem {
  id: number;
  documentId: string;
  scanStatus: string;
  reportTime: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revokedAt: string | null;
  workItem: {
    id: number;
    documentId: string;
    name: string;
    scanMode: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    isCompleted: boolean;
    plannedCompletionDate: string | null;
  };
  employee: {
    id: number;
    documentId: string;
    name: string;
    gender: string | null;
    birthday: string | null;
    bloodType: string | null;
    nationalIdNo: string | null;
    motorbikeLicense: string | null;
    carLicense: string | null;
    email: string | null;
    emergencyContact: string | null;
    emergencyContactRelationship: string | null;
    emergencyContactPhoneNumber: string | null;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    note: string | null;
  };
  steelAssembly: Array<{
    steelAssemblyId: string;
    scanHistoryId: string;
    steelAssembly: {
      id: string;
      steelAssemblyCode: string | null;
      steelAssemblyId: string | null;
    } | null;
  }>;
}

interface Employee {
  id: string;
  name: string;
  documentId: string;
}

interface DragDropListProps {
  workItems: WorkItem[];
  employees: Employee[];
  fetchNodeData?: () => Promise<void>;
  totalSteelAssemblies?: number;
  projectId: string;
}

const DraggableItem: React.FC<{
  id: string;
  title: string;
  content: React.ReactNode;
  progressData?: { success: number; total: number };
}> = ({ id, title, content, progressData }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: "#fafafa",
    padding: "10px",
    marginBottom: "8px",
    borderRadius: "8px",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="w-full">
        <Collapse className="w-full" defaultActiveKey={["1"]}>
          <Panel header={title} key={id}>
            <Typography.Text>{content}</Typography.Text>
          </Panel>
        </Collapse>
        {progressData && (
          <div className="mt-2 px-2">
            <Progress
              percent={
                progressData.total > 0
                  ? Math.round(
                      (progressData.success / progressData.total) * 100,
                    )
                  : 0
              }
              format={() => `${progressData.success}/${progressData.total}`}
              size="small"
              status={
                progressData.success === progressData.total &&
                progressData.total > 0
                  ? "success"
                  : "active"
              }
            />
          </div>
        )}
      </div>
      {/* Drag Handle */}
      <span {...listeners} className="cursor-grab px-3">
        <IconGripVertical size={20} />
      </span>
    </div>
  );
};

const getScanModeLabel = (scanMode: string, t: any) => {
  const scanModeMap: { [key: string]: string } = {
    multiple: t("dragDropList.scanModeMultiple") || "複數",
    single: t("dragDropList.scanModeSingle") || "單支",
    all: t("dragDropList.scanModeAll") || "全部",
  };
  return scanModeMap[scanMode] || "N/A";
};

const getSortedWorkItems = (wItems: WorkItem[]): WorkItem[] => {
  if (wItems.length === 0) return wItems;
  if (wItems.some((item) => item.order !== undefined && item.order !== null)) {
    return [...wItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  const nodeId = wItems[0]?.projectNodeId;
  const savedOrder = nodeId
    ? localStorage.getItem(`workItemOrder_${nodeId}`)
    : null;
  if (!savedOrder) return wItems;
  const orderedIds: string[] = JSON.parse(savedOrder);
  return [...wItems].sort((a, b) => {
    const aIndex = orderedIds.indexOf(a.id);
    const bIndex = orderedIds.indexOf(b.id);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

const DragDropList: React.FC<DragDropListProps> = ({
  workItems = [],
  employees,
  fetchNodeData,
  totalSteelAssemblies = 0,
  projectId,
}) => {
  const { t } = useTranslation();
  const [scanHistoryData, setScanHistoryData] = useState<{
    [key: string]: ScanHistoryItem[];
  }>({});

  const [workItemReadData, setWorkItemReadData] = useState<{
    [key: string]: any;
  }>({});

  const fetchScanHistory = async (workItemId: string) => {
    if (!workItemId) {
      console.warn("Work item ID is undefined, skipping scan history fetch");
      return;
    }

    try {
      console.log(`Fetching scan history for work item: ${workItemId}`);

      const response = await orpcClient.scanHistory.list({
        filter: { workItemId: workItemId },
      });

      const allData = (response || []) as unknown as ScanHistoryItem[];

      console.log(
        `Fetched all ${allData.length} scan history records for work item ${workItemId}`,
      );
      setScanHistoryData((prev) => ({
        ...prev,
        [workItemId]: allData,
      }));
    } catch (error) {
      console.error(
        `Error fetching scan history for work item ${workItemId}:`,
        error,
      );
      setScanHistoryData((prev) => ({
        ...prev,
        [workItemId]: [],
      }));
    }
  };

  console.log("workItems2", workItems);

  const fetchWorkItemReadData = async (
    workItemId: string,
    projectNodeId: string,
  ) => {
    try {
      const response = await single(
        orpcClient.workItem.read([{ id: workItemId }]),
      );
      setWorkItemReadData((prev) => ({
        ...prev,
        [workItemId]: response,
      }));
    } catch (error) {
      console.error(`Error fetching workItem read for ${workItemId}:`, error);
    }
  };

  useEffect(() => {
    workItems.forEach((item) => {
      console.log("Work item:", item.name, "documentId:", item.id);
      if (item.id) {
        fetchScanHistory(item.id);
        fetchWorkItemReadData(item.id, item.projectNodeId);
      }
    });
  }, [workItems]);
  const getScanStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      success: t("dragDropList.statusSuccess") || "成功",
      error: t("dragDropList.statusError") || "錯誤",
    };
    return statusMap[status] || status;
  };

  const getProgressData = (workItemId: string) => {
    const workItemData = workItemReadData[workItemId];
    const steelAssemblies: Array<{ status: string }> =
      workItemData?.steelAssembly || [];
    const successCount = steelAssemblies.filter(
      (item) => item.status === "success",
    ).length;
    return { success: successCount, total: totalSteelAssemblies };
  };

  const getScanHistoryTable = (workItemDocumentId: string) => {
    const history = scanHistoryData[workItemDocumentId] || [];
    if (history.length === 0)
      return <div>{t("dragDropList.noScanRecord") || "無掃描記錄"}</div>;

    // Sort by reportTime in descending order (newest first)
    const sortedHistory = [...history].sort(
      (a, b) => dayjs(b.reportTime).valueOf() - dayjs(a.reportTime).valueOf(),
    );

    const columns = [
      {
        title: t("dragDropList.reportPerson") || "回報人員",
        dataIndex: ["employee", "name"],
        key: "employeeName",
        render: (name: string) => name || "N/A",
      },
      {
        title: t("dragDropList.reportStatus") || "回報狀態",
        dataIndex: "scanStatus",
        key: "scanStatus",
        render: (status: string) => getScanStatusLabel(status),
      },
      {
        title: t("dragDropList.reportComponent") || "回報構件",
        dataIndex: "steelAssembly",
        key: "steelAssembly",
        render: (steelAssemblyList: Array<any>) => {
          console.log("steelAssemblyList", steelAssemblyList);
          if (!steelAssemblyList || steelAssemblyList.length === 0)
            return "N/A";
          const names = steelAssemblyList
            .map((assembly) => {
              return (
                assembly?.steelAssembly?.steelAssemblyCode ||
                assembly?.steelAssemblyCode ||
                assembly?.steelAssembly?.steelAssemblyId
              );
            })
            .join(", ");
          const count = steelAssemblyList.length;
          return (
            <div>
              <div>{names}</div>
              <div className="text-gray-500 text-sm">
                ({count}
                {t("dragDropList.componentsCount") || "個構件"})
              </div>
            </div>
          );
        },
      },
      {
        title: t("dragDropList.reportTime") || "回報時間",
        dataIndex: "reportTime",
        key: "reportTime",
        render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        title: t("dragDropList.action") || "操作",
        key: "action",
        render: (_: any, record: ScanHistoryItem) => {
          if (record.revokedAt) {
            return (
              <Badge variant="light" color="gray" size="lg">
                {t("dragDropList.revoked") || "已取消"}
              </Badge>
            );
          }
          return (
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={() => handleRevoke(record, workItemDocumentId)}
            >
              {t("dragDropList.revokeReport") || "取消回報"}
            </Button>
          );
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={sortedHistory.map((item, index) => ({
          ...item,
          key: index,
        }))}
        pagination={false}
        size="small"
      />
    );
  };

  const getReportItemsSection = (item: WorkItem) => {
    const texts = item.reportPresetTexts?.texts || [];
    return (
      <div className="mb-4">
        <div className="font-bold mb-2" style={{ fontSize: "1.2rem" }}>
          {t("dragDropList.reportItems") || "回報項目"}：
        </div>
        <div className="flex flex-wrap gap-1">
          {texts.length > 0
            ? texts.map((text) => (
                <Badge key={text} variant="light" color="cyan" size="lg">
                  {text}
                </Badge>
              ))
            : t("dragDropList.noReportItems") || "無回報項目"}
        </div>
      </div>
    );
  };
  console.log("workItems", workItems);

  const [items, setItems] = useState(
    getSortedWorkItems(workItems).map((item) => {
      return {
        id: item.id,
        title: item.name,
        content: (
          <div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="font-bold" style={{ fontSize: "1.2rem" }}>
                  {t("dragDropList.scanMode") || "掃描模式"}：
                </div>
                <div>{getScanModeLabel(item.scanMode, t)}</div>
              </div>

              <div>
                <div className="font-bold" style={{ fontSize: "1.2rem" }}>
                  {t("dragDropList.personnelSelection") || "人員選擇"}：
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(item.employees || []).length > 0
                    ? (item.employees || []).map((emp) => (
                        <Badge
                          key={emp.id}
                          variant="light"
                          color="blue"
                          size="lg"
                        >
                          {emp.name}
                        </Badge>
                      ))
                    : "N/A"}
                </div>
              </div>
            </div>

            {getReportItemsSection(item)}

            <div className="mb-4">
              <div className="font-bold mb-2" style={{ fontSize: "1.2rem" }}>
                {t("dragDropList.scanRecord") || "掃描記錄"}：
              </div>
              {getScanHistoryTable(item.id)}
            </div>

            <div
              className="flex justify-center gap-2"
              style={{ margin: "1rem" }}
            >
              <Button
                variant="filled"
                color="gray"
                onClick={() => handleEditItem(item)}
              >
                {t("dragDropList.editWorkItem") || "編輯工項"}
              </Button>
              <Button color="red" onClick={() => handleDelete(item)}>
                {t("dragDropList.deleteWorkItem") || "刪除工項"}
              </Button>
            </div>
          </div>
        ),
      };
    }),
  );

  useEffect(() => {
    setItems(
      getSortedWorkItems(workItems).map((item) => {
        return {
          id: item.id,
          title: item.name,
          content: (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="font-bold" style={{ fontSize: "1.2rem" }}>
                    {t("dragDropList.scanMode") || "掃描模式"}：
                  </div>
                  <div>{getScanModeLabel(item.scanMode, t)}</div>
                </div>

                <div>
                  <div className="font-bold" style={{ fontSize: "1.2rem" }}>
                    {t("dragDropList.personnelSelection") || "人員選擇"}：
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(item.employees || []).length > 0
                      ? (item.employees || []).map((emp) => (
                          <Badge
                            key={emp.id}
                            variant="light"
                            color="blue"
                            size="lg"
                          >
                            {emp.name}
                          </Badge>
                        ))
                      : "N/A"}
                  </div>
                </div>
              </div>

              {getReportItemsSection(item)}

              <div className="mb-4">
                <div className="font-bold mb-2" style={{ fontSize: "1.2rem" }}>
                  {t("dragDropList.scanRecord") || "掃描記錄"}：
                </div>
                {getScanHistoryTable(item.id)}
              </div>

              <div
                className="flex justify-center gap-2"
                style={{ margin: "1rem" }}
              >
                <Button
                  variant="filled"
                  color="gray"
                  onClick={() => handleEditItem(item)}
                >
                  {t("dragDropList.editWorkItem") || "編輯工項"}
                </Button>
                <Button color="red" onClick={() => handleDelete(item)}>
                  {t("dragDropList.deleteWorkItem") || "刪除工項"}
                </Button>
              </div>
            </div>
          ),
        };
      }),
    );
  }, [workItems, scanHistoryData]);

  const [tempItemData, setTempItemData] = useState<WorkItem>();
  const [originalEmployees, setOriginalEmployees] = useState<Employee[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingWorkItem, setIsSavingWorkItem] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<WorkItem | null>(null);
  const [scanToRevoke, setScanToRevoke] = useState<{
    scan: ScanHistoryItem;
    workItemId: string;
  } | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const handleRevoke = (scan: ScanHistoryItem, workItemId: string) => {
    setScanToRevoke({ scan, workItemId });
  };

  const confirmRevoke = async () => {
    if (!scanToRevoke) return;
    setIsRevoking(true);
    try {
      await single(
        orpcClient.scanHistory.revoke([{ id: String(scanToRevoke.scan.id) }]),
      );

      await fetchScanHistory(scanToRevoke.workItemId);
      const targetWorkItem = workItems.find(
        (wi) => wi.id === scanToRevoke.workItemId,
      );
      if (targetWorkItem) {
        await fetchWorkItemReadData(
          targetWorkItem.id,
          targetWorkItem.projectNodeId,
        );
      }
      setScanToRevoke(null);
      fetchNodeData?.();
    } catch (error) {
      console.error("Error revoking scan history:", error);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleEditItem = (item: WorkItem) => {
    setTempItemData(item);
    setOriginalEmployees(item.employees || []);
    setIsEditModalOpen(true);
  };
  const handleSubmit = async (formValue: WorkItemDialogValue) => {
    if (!tempItemData?.id) return;

    setIsSavingWorkItem(true);
    try {
      await single(
        orpcClient.workItem.update([
          {
            id: tempItemData.id,
            name: formValue.name,
            scanMode: formValue.scanMode,
            reportPresetTexts:
              formValue.reportTexts.length > 0
                ? { texts: formValue.reportTexts }
                : null,
          },
        ]),
      );

      const originalIds = originalEmployees.map((e) => e.id);
      const newIds = formValue.employees;

      const toRemove = originalIds.filter((id) => !newIds.includes(id));
      const toAdd = newIds.filter((id) => !originalIds.includes(id));

      if (toRemove.length > 0) {
        await orpcClient.workItem.removeEmployee({
          workItemId: tempItemData.id,
          employeeId: toRemove,
        });
      }
      if (toAdd.length > 0) {
        await orpcClient.workItem.addEmployee({
          workItemId: tempItemData.id,
          employeeId: toAdd,
        });
      }

      setIsEditModalOpen(false);
      fetchNodeData?.();
    } catch (error) {
      console.error("Error updating work item:", error);
    } finally {
      setIsSavingWorkItem(false);
    }
  };

  const handleDelete = (item: WorkItem) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await single(
        orpcClient.workItem.delete([{ id: itemToDelete.id }]),
      );
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchNodeData?.();
    } catch (error) {
      console.error("Error deleting work item:", error);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    const nodeId = workItems[0]?.projectNodeId;
    if (nodeId) {
      localStorage.setItem(
        `workItemOrder_${nodeId}`,
        JSON.stringify(newItems.map((item) => item.id)),
      );

      allOrThrow(
        orpcClient.workItem.update(
          newItems.map((item, index) => ({
            id: item.id,
            order: index,
          })),
        ),
      ).catch((error) => {
        console.error("Error reordering work items:", error);
      });
    }
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((item) => {
            const workItem = workItems.find((wi) => wi.id === item.id);
            const progressData = workItem
              ? getProgressData(workItem.id)
              : undefined;
            return (
              <DraggableItem
                key={item.id}
                id={item.id}
                title={item.title}
                content={item.content}
                progressData={progressData}
                //onEdit={() => handleEditItem(workItems.find((wi) => wi.id === item.id)!)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <Modal
        open={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        width={400}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => {
              setIsDeleteModalOpen(false);
              setItemToDelete(null);
            }}
          >
            {t("dragDropList.cancel") || "取消"}
          </Button>,
          <Button key="confirm" color="red" onClick={confirmDelete}>
            {t("dragDropList.confirmDelete") || "確定刪除"}
          </Button>,
        ]}
      >
        <div className="py-3 text-xl font-bold">
          {t("dragDropList.deleteConfirmTitle") || "確認刪除工項"}
        </div>
        <div>
          {t("dragDropList.deleteConfirmMessage") || "確定要刪除工項"}{" "}
          <strong>{itemToDelete?.name}</strong>？
        </div>
      </Modal>
      <Modal
        open={!!scanToRevoke}
        onCancel={() => setScanToRevoke(null)}
        width={400}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => setScanToRevoke(null)}
          >
            {t("dragDropList.cancel") || "取消"}
          </Button>,
          <Button
            key="confirm"
            color="red"
            loading={isRevoking}
            onClick={confirmRevoke}
          >
            {t("dragDropList.confirmRevoke") || "確定取消回報"}
          </Button>,
        ]}
      >
        <div className="py-3 text-xl font-bold">
          {t("dragDropList.revokeConfirmTitle") || "確認取消回報"}
        </div>
        <div>
          {t("dragDropList.revokeConfirmMessage") ||
            "確定要取消這筆回報紀錄嗎？取消後將重新計算構件狀態。"}
        </div>
      </Modal>
      <WorkItemDialog
        open={isEditModalOpen}
        mode="edit"
        employees={employees.map((emp) => ({ id: emp.id, name: emp.name }))}
        confirmLoading={isSavingWorkItem}
        initialValue={
          tempItemData
            ? {
                name: tempItemData.name,
                employees: (tempItemData.employees || []).map((emp) => emp.id),
                scanMode:
                  (tempItemData.scanMode as "multiple" | "single" | "all") ||
                  "multiple",
                reportTexts: tempItemData.reportPresetTexts?.texts || [],
              }
            : null
        }
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleSubmit}
      />
      {/*
      <Modal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        width={600}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => setIsEditModalOpen(false)}
          >
            {t("dragDropList.cancel") || "取消"}
          </Button>,
          <Button key="submit" variant="primary" onClick={handleSubmit}>
            {t("dragDropList.confirmEdit") || "確定編輯"}
          </Button>,
        ]}
      >
        <div className="py-3 text-2xl font-bold">
          {t("dragDropList.editWorkItem") || "編輯工項"}
        </div>

        <div className="flex">
          <div className="py-4 pl-2">
            <div style={{ color: "#6F6F6F" }}>
              {t("dragDropList.workItemSelection") || "工項選擇"}
            </div>
            <Input
              placeholder={
                t("dragDropList.enterWorkItemName") || "請輸入工項名稱"
              }
              value={tempItemData?.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTempItemData((prev) =>
                  prev ? { ...prev, name: e.target.value } : undefined,
                )
              }
            />
          </div>
        </div>
        <div className="py-4 pl-2">
          <div style={{ color: "#6F6F6F" }}>
            {t("dragDropList.personnelSelection") || "人員選擇"}
          </div>
          <Select
            mode="multiple"
            placeholder={t("dragDropList.selectPersonnel") || "請選擇人員"}
            value={tempItemData?.employees?.map((data) => data?.id)}
            style={{ minWidth: "200px" }}
            options={employees.map((emp) => ({
              label: emp.name,
              value: emp.id,
            }))}
            onChange={(value) => {
              setTempItemData((prev) => {
                if (!prev) return undefined;
                const selectedEmployees = employees.filter((emp) =>
                  value.includes(emp.id),
                );
                return { ...prev, employees: selectedEmployees };
              });
            }}
          />
        </div>
        <div className="py-4 pl-2">
          <div style={{ color: "#6F6F6F" }}>
            {t("dragDropList.scanPassCount") || "掃描通過支數"}
          </div>
          <Select
            value={tempItemData?.scanMode}
            options={[
              {
                label: t("dragDropList.scanModeMultiple") || "複數",
                value: "multiple",
              },
              {
                label: t("dragDropList.scanModeSingle") || "單支",
                value: "single",
              },
              { label: t("dragDropList.scanModeAll") || "全部", value: "all" },
            ]}
            onChange={(value) =>
              setTempItemData((prev) =>
                prev ? { ...prev, scanMode: value } : undefined,
              )
            }
            placeholder={
              t("dragDropList.selectScanPassCount") || "請選擇掃描通過支數"
            }
          />
        </div>
      </Modal>
      */}
    </>
  );
};

export default DragDropList;
